import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Save, Send, FileDown, AlertCircle, ChevronDown, CheckCircle, Clock } from 'lucide-react';

/**
 * Step Navigation Component
 * Bottom navigation with Previous, Save Draft, Next, and Final actions
 * 
 * ⭐ H2: Enhanced with validation feedback and disabled state messaging
 * 
 * @param {Object} props
 * @param {number} props.currentStep - Current step index
 * @param {Array} props.steps - Array of step objects
 * @param {Function} props.onPrevious - Handler for previous button
 * @param {Function} props.onNext - Handler for next button
 * @param {Function} props.onSaveDraft - Handler for save draft
 * @param {Function} props.onGenerateQuote - Handler for final quote generation
 * @param {Function} props.onDownloadPDF - Handler for PDF download
 * @param {boolean} props.canSaveDraft - Whether draft can be saved
 * @param {boolean} props.canProceed - Whether user can proceed to next step
 * @param {boolean} props.saving - Loading state for saving
 * @param {boolean} props.savingDraft - Loading state for draft saving
 * @param {boolean} props.generatingPDF - Loading state for PDF generation
 * @param {string} props.quoteNumber - Current quote number
 * @param {string} props.quoteId - Current quote ID
 * @param {string} props.quoteStatus - Current quote status (draft, pending_approval, approved, final, sent)
 * @param {Function} props.onStatusChange - Callback when quote status changes
 * @param {string} props.validationError - Current validation error message
 */
const StepNavigation = ({
  currentStep,
  steps,
  onPrevious,
  onNext,
  onSaveDraft,
  onGenerateQuote,
  onDownloadPDF,
  canSaveDraft,
  canProceed,
  saving,
  savingDraft,
  generatingPDF,
  quoteNumber,
  quoteId,
  quoteStatus,
  onStatusChange,
  validationError
}) => {
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // ⭐ H2: Get hint text for disabled Next button
  const getNextButtonHint = () => {
    if (canProceed) return null;
    
    // Check which step we're on and what's missing
    switch (currentStep) {
      case 0: // Deal Info
        return 'Complete customer name and solution area';
      case 1: // Platform
        return 'Select a platform and deployment type';
      case 3: // Products
        return 'Select at least one product';
      default:
        return 'Complete required fields to continue';
    }
  };

  const nextHint = getNextButtonHint();

  return (
    <div className="mt-6">
      {/* ⭐ H2: Show hint when Next is disabled */}
      {!canProceed && !isLastStep && nextHint && (
        <div className="mb-4 flex items-center gap-2 text-amber-600 text-sm">
          <AlertCircle size={16} />
          <span>{nextHint}</span>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        {/* Left Side - Previous & Save Draft */}
        <div className="flex items-center gap-4">
          {/* Previous Button */}
          <button
            onClick={onPrevious}
            disabled={isFirstStep}
            className="px-6 py-3 text-slate-600 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2 rounded-xl hover:bg-slate-100 transition-colors"
          >
            {!isFirstStep && (
              <>
                <ChevronLeft size={18} />
                <span>Previous: {steps[currentStep - 1].name}</span>
              </>
            )}
          </button>
          
          {/* Save Draft Button - shown on non-final steps only */}
          {canSaveDraft && !isLastStep && (
            <button
              onClick={onSaveDraft}
              disabled={savingDraft}
              className="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {savingDraft ? 'Saving...' : 'Save Draft'}
            </button>
          )}
        </div>
        
        {/* Right Side - Next or Final Actions */}
        {!isLastStep ? (
          <div className="flex items-center gap-3">
            {/* ⭐ H2: Visual indicator when disabled */}
            {!canProceed && (
              <span className="text-xs text-amber-500 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
                Complete required fields
              </span>
            )}

            <button
              onClick={onNext}
              disabled={!canProceed}
              className={`px-8 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                canProceed
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-lg hover:shadow-blue-200'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
              title={!canProceed ? nextHint : ''}
            >
              <span>Next: {steps[currentStep + 1].name}</span>
              <ChevronRight size={18} />
            </button>
          </div>
        ) : (
          <FinalStepActions
            onDownloadPDF={onDownloadPDF}
            onGenerateQuote={onGenerateQuote}
            onSaveDraft={onSaveDraft}
            generatingPDF={generatingPDF}
            saving={saving}
            savingDraft={savingDraft}
            canSaveDraft={canSaveDraft}
            quoteNumber={quoteNumber}
            quoteId={quoteId}
            quoteStatus={quoteStatus}
            onStatusChange={onStatusChange}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Final Step Actions Sub-component
 * Clean 3-button layout: Save Draft, Generate PDF (dropdown), Submit Quote
 * Shows status badges for approved/pending/sent quotes
 */
const FinalStepActions = ({
  onDownloadPDF,
  onGenerateQuote,
  onSaveDraft,
  generatingPDF,
  saving,
  savingDraft,
  canSaveDraft,
  quoteNumber,
  quoteId,
  quoteStatus,
  onStatusChange
}) => {
  const [showPdfDropdown, setShowPdfDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Check quote states
  const isFinalized = ['final', 'approved'].includes(quoteStatus);
  const isPendingApproval = quoteStatus === 'pending_approval';
  const isSent = quoteStatus === 'sent';
  const isAccepted = quoteStatus === 'accepted';
  const isLost = quoteStatus === 'lost';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPdfDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePdfDownload = (type) => {
    onDownloadPDF(type, quoteNumber || 'DRAFT', quoteId, onStatusChange);
    setShowPdfDropdown(false);
  };

  return (
    <div className="flex items-center gap-3">
      {/* Status Badge for finalized quotes */}
      {isFinalized && (
        <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-xl font-medium">
          <CheckCircle size={18} />
          <span>Quote {quoteStatus === 'approved' ? 'Approved' : 'Finalized'}</span>
        </div>
      )}

      {/* Status Badge for pending approval */}
      {isPendingApproval && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-xl font-medium">
          <Clock size={18} />
          <span>Pending Approval</span>
        </div>
      )}

      {/* Status Badge for sent quotes */}
      {isSent && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-xl font-medium">
          <CheckCircle size={18} />
          <span>Sent to Customer</span>
        </div>
      )}

      {/* Status Badge for accepted quotes */}
      {isAccepted && (
        <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-xl font-medium">
          <CheckCircle size={18} />
          <span>Won</span>
        </div>
      )}

      {/* Status Badge for lost quotes */}
      {isLost && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-xl font-medium">
          <Clock size={18} />
          <span>Lost</span>
        </div>
      )}

      {/* Save Draft - only for draft quotes */}
      {canSaveDraft && !isFinalized && !isPendingApproval && (
        <button
          onClick={onSaveDraft}
          disabled={savingDraft}
          className="px-6 py-3 border-2 border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={18} />
          {savingDraft ? 'Saving...' : 'Save Draft'}
        </button>
      )}

      {/* Generate PDF Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowPdfDropdown(!showPdfDropdown)}
          disabled={generatingPDF}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-200 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FileDown size={18} />
          {generatingPDF ? 'Generating...' : 'Generate PDF'}
          <ChevronDown size={16} className={`transition-transform ${showPdfDropdown ? 'rotate-180' : ''}`} />
        </button>

        {showPdfDropdown && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
            {/* Internal PDF - always available */}
            <button
              onClick={() => handlePdfDownload('internal')}
              className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
            >
              <div className="font-semibold text-slate-800 text-sm">Internal PDF</div>
              <div className="text-xs text-slate-500">With SKUs & pricing details</div>
            </button>
            {/* Customer PDF - only for approved/final/sent/accepted quotes */}
            {(isFinalized || isSent || isAccepted) ? (
              <button
                onClick={() => handlePdfDownload('customer')}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
              >
                <div className="font-semibold text-slate-800 text-sm">Customer PDF</div>
                <div className="text-xs text-slate-500">Summary for customer</div>
              </button>
            ) : (
              <div className="w-full px-4 py-3 border-t border-slate-100 bg-slate-50">
                <div className="font-semibold text-slate-400 text-sm">Customer PDF</div>
                <div className="text-xs text-slate-400">Requires approval first</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit Quote - only for draft quotes */}
      {!isFinalized && !isPendingApproval && (
        <button
          onClick={onGenerateQuote}
          disabled={saving}
          className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-green-200 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Submit quote for approval"
        >
          <Send size={18} />
          {saving ? 'Submitting...' : 'Submit Quote'}
        </button>
      )}
    </div>
  );
};

export default StepNavigation;
